import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listingWizardService } from "@/lib/api/listingWizardService";
import { toast } from "sonner";
import { Listing } from "@/lib/types";
import { AxiosProgressEvent } from "axios"; // Import AxiosProgressEvent

// Query keys for the listing wizard
export const listingWizardKeys = {
  draft: (id?: number) => (id ? ["draftListing", id] : ["draftListing"]),
  propertyTypes: ["propertyTypes"],
  locations: ["locations"],
};

// React Query hooks for the listing wizard
export function useListingWizard() {
  const queryClient = useQueryClient();

  // Step 1: Create draft listing
  const createDraftMutation = useMutation({
    mutationFn: async (data: {
      property_type_id: number; // Changed to property_type_id (number) to match apiData
      listing_type: string; // Changed to listing_type to match apiData
      price: number;
      currency: string;
      price_period?: string;
      bathrooms?: number;
      deposit_months?: number; // Changed to deposit_months to match apiData
      features?: string[];
      floor_size?: number; // Changed to floor_size to match apiData
      furnishing?: string;
      location_id?: number; // Changed to location_id to match apiData
      address?: string; // Added address to match apiData
      latitude?: number; // Added latitude to match apiData
      longitude?: number; // Added longitude to match apiData
      minimum_term_months?: number; // Changed to minimum_term_months to match apiData
      parking_spaces?: number; // Changed to parking_spaces to match apiData
      plot_size?: number; // Changed to plot_size to match apiData
      rooms?: number;
      availability_status?: string;
      yearBuilt?: number; // Changed to yearBuilt to match frontend form
      title?: string;
      description?: string;
      num_floors?: number; // Added num_floors
      advance_payment_months?: number; // Added advance_payment_months
      short_term_available?: boolean | number; // Added short_term_available
      bills_included?: boolean | number; // Added bills_included
      amenities?: number[]; // Added amenities
      contact_name?: string; // Added contact fields
      contact_phone?: string;
      contact_email?: string;
      contact_whatsapp?: boolean | number;
      contact_botim?: boolean | number;
      hide_contact?: boolean | number;
      cplk_ref?: string; // Add cplk_ref
    }) => {
      console.log(
        "[useListingWizard] createDraftMutation.mutationFn: Called with data:",
        JSON.stringify(data)
      );
      try {
        const result = await listingWizardService.createDraftListing(data);
        console.log(
          "[useListingWizard] createDraftMutation.mutationFn: Service call result:",
          JSON.stringify(result)
        );
        return result;
      } catch (error) {
        console.error(
          "[useListingWizard] createDraftMutation.mutationFn: Error calling service:",
          error
        );
        throw error; // Re-throw to be caught by react-query's onError
      }
    },
    onSuccess: (data) => {
      console.log(
        "[useListingWizard] createDraftMutation.onSuccess: Called with data:",
        JSON.stringify(data)
      );
      toast.success("Draft listing created!");
      // Ensure data and data.listing.id are valid before using them
      // The service now returns the Listing object directly, so data.listing.id should be present.
      if (data && data.listing && data.listing.id) {
        queryClient.setQueryData(
          listingWizardKeys.draft(Number(data.listing.id)),
          data
        );
      } else {
        console.error(
          "[useListingWizard] createDraftMutation.onSuccess: Data or data.listing.id is missing.",
          data
        );
        // Potentially toast an error here as well, as this is unexpected.
        toast.error("Draft created, but response was unexpected.");
      }
    },
    onError: (error) => {
      console.error(
        "[useListingWizard] createDraftMutation.onError: Called with error:",
        error
      );
      toast.error("Failed to create draft listing");
      // console.error(error); // Original console.error is fine, this is more specific.
    },
  });

  // Step 2: Update property details
  const updateDetailsMutation = useMutation({
    mutationFn: ({
      listingId,
      details,
    }: {
      listingId: number;
      details: {
        size?: number;
        size_unit?: string;
        floor_size?: number;
        plot_size?: number;
        rooms?: number;
        bedrooms?: number;
        bathrooms?: number;
        parking_spaces?: number;
        num_floors?: number;
        furnishing?: string;
        yearBuilt?: number; // Changed to yearBuilt to match frontend form
        availability_status?: string;
        advance_payment_months?: number;
        minimum_term_months?: number;
        minimum_term_unit?: "months" | "years";
        deposit_months?: number;
        short_term_available?: boolean;
        bills_included?: boolean;
        attributes?: number[];
        cplk_ref?: string; // Add cplk_ref
      };
    }) => listingWizardService.updatePropertyDetails(listingId, details),
    onSuccess: (data) => {
      // 'data' here is ListingCore
      toast.success("Property details saved!");
      queryClient.setQueryData(
        listingWizardKeys.draft(Number(data.id)),
        (oldListing: Listing | undefined) => {
          if (oldListing) {
            return {
              ...oldListing,
              listing: {
                ...oldListing.listing,
                ...data, // Merge the updated ListingCore data
              },
            };
          }
          return oldListing;
        }
      );
    },
    onError: (error) => {
      toast.error("Failed to save property details");
      console.error(error);
    },
  });

  // Step 3: Update location
  const updateLocationMutation = useMutation({
    mutationFn: ({
      listingId,
      locationData,
    }: {
      listingId: number;
      locationData: any;
    }) => listingWizardService.updateLocation(listingId, locationData),
    onSuccess: (data) => {
      // 'data' here is ListingCore
      toast.success("Location saved!");
      queryClient.setQueryData(
        listingWizardKeys.draft(Number(data.id)),
        (oldListing: Listing | undefined) => {
          if (oldListing) {
            return {
              ...oldListing,
              listing: {
                ...oldListing.listing,
                ...data, // Merge the updated ListingCore data
              },
            };
          }
          return oldListing;
        }
      );
    },
    onError: (error) => {
      toast.error("Failed to save location");
      console.error(error);
    },
  });

  // Step 4: Upload image
  const uploadImageMutation = useMutation({
    mutationFn: ({
      listingId,
      imageFile,
      isPrimary,
      onUploadProgress, // Added onUploadProgress parameter
    }: {
      listingId: number;
      imageFile: File;
      isPrimary: boolean;
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void; // Changed type to AxiosProgressEvent
    }) =>
      listingWizardService.uploadImage(
        listingId,
        imageFile,
        isPrimary,
        onUploadProgress
      ), // Pass onUploadProgress to service
    onSuccess: (data, variables) => {
      console.log("[uploadImageMutation] onSuccess - data:", data);
      console.log("[uploadImageMutation] onSuccess - variables:", variables);
      toast.success("Image uploaded successfully!");
      // Optimistically update the listing data to include the new image
      queryClient.setQueryData(
        listingWizardKeys.draft(variables.listingId),
        (oldListing: Listing | undefined) => {
          console.log(
            "[uploadImageMutation] setQueryData callback - oldListing:",
            oldListing
          );
          if (oldListing) {
            // Ensure images array exists and add the new image
            const updatedImages = oldListing.images
              ? [...oldListing.images, data.data.image]
              : [data.data.image];
            return { ...oldListing, images: updatedImages };
          }
          return oldListing;
        }
      );
      console.log("[uploadImageMutation] Data.data.image:", data.data.image);
      console.log(
        "[uploadImageMutation] Updated images in cache:",
        (
          queryClient.getQueryData(
            listingWizardKeys.draft(variables.listingId)
          ) as Listing | undefined
        )?.images
      );
      // Re-adding invalidateQueries to ensure ReviewStep gets latest data
      queryClient.invalidateQueries({
        queryKey: listingWizardKeys.draft(variables.listingId),
      });
    },
    onError: (error) => {
      toast.error("Failed to upload image");
      console.error(error);
    },
  });

  // Delete image
  const deleteImageMutation = useMutation({
    mutationFn: ({
      listingId,
      imageId,
    }: {
      listingId: number;
      imageId: number;
    }) => listingWizardService.deleteImage(listingId, imageId),
    onSuccess: (data, variables) => {
      console.log("[deleteImageMutation] onSuccess - data:", data);
      console.log("[deleteImageMutation] onSuccess - variables:", variables);
      toast.success("Image deleted!");
      // Optimistically update the listing data by removing the deleted image
      queryClient.setQueryData(
        listingWizardKeys.draft(variables.listingId),
        (oldListing: Listing | undefined) => {
          console.log(
            "[deleteImageMutation] setQueryData callback - oldListing:",
            oldListing
          );
          if (oldListing && oldListing.images) {
            const updatedImages = oldListing.images.filter(
              (img) => img.id !== variables.imageId
            );
            return { ...oldListing, images: updatedImages };
          }
          return oldListing;
        }
      );
      // Re-adding invalidateQueries to ensure ReviewStep gets latest data
      queryClient.invalidateQueries({
        queryKey: listingWizardKeys.draft(variables.listingId),
      });
    },
    onError: (error) => {
      toast.error("Failed to delete image");
      console.error(error);
    },
  });

  // Set image as primary
  const setPrimaryImageMutation = useMutation({
    mutationFn: ({
      listingId,
      imageId,
    }: {
      listingId: number;
      imageId: number;
    }) => listingWizardService.setImageAsPrimary(listingId, imageId),
    onSuccess: (data, variables) => {
      toast.success("Primary image updated!");
      // Optimistically update the listing data to reflect the new primary image
      queryClient.setQueryData(
        listingWizardKeys.draft(variables.listingId),
        (oldListing: Listing | undefined) => {
          if (oldListing && oldListing.images) {
            const updatedImages = oldListing.images.map((img) => {
              const isPrimary = Number(img.id) === variables.imageId;
              console.log(
                `[setPrimaryImageMutation] Image ID: ${
                  img.id
                } (Type: ${typeof img.id}), Target Image ID: ${
                  variables.imageId
                } (Type: ${typeof variables.imageId}), Comparison Result: ${isPrimary}`
              );
              return {
                ...img,
                is_primary: isPrimary, // Ensure numeric comparison
              };
            });
            return { ...oldListing, images: updatedImages };
          }
          return oldListing;
        }
      );
      // Re-adding invalidateQueries to ensure ReviewStep gets latest data
      queryClient.invalidateQueries({
        queryKey: listingWizardKeys.draft(variables.listingId),
      });
    },
    onError: (error) => {
      toast.error("Failed to update primary image");
      console.error(error);
    },
  });

  // Step 5: Update contact info
  const updateContactMutation = useMutation({
    mutationFn: ({
      listingId,
      contactInfo,
    }: {
      listingId: number;
      contactInfo: {
        contact_name?: string;
        contact_phone?: string;
        contact_email?: string;
        contact_whatsapp?: boolean;
        contact_botim?: boolean;
        hide_contact?: boolean;
        cplk_ref?: string; // Add cplk_ref
      };
    }) => listingWizardService.updateContactInfo(listingId, contactInfo),
    onSuccess: (data, variables) => {
      toast.success("Contact information saved!");
      // Optimistically update the listing data to include the new contact info
      queryClient.setQueryData(
        listingWizardKeys.draft(Number(variables.listingId)),
        (oldListing: Listing | undefined) => {
          if (oldListing) {
            return {
              ...oldListing,
              contact_name: variables.contactInfo.contact_name,
              contact_phone: variables.contactInfo.contact_phone,
              contact_email: variables.contactInfo.contact_email,
              contact_whatsapp: variables.contactInfo.contact_whatsapp,
              contact_botim: variables.contactInfo.contact_botim,
              hide_contact: variables.contactInfo.hide_contact,
            };
          }
          return oldListing;
        }
      );
      queryClient.invalidateQueries({
        queryKey: listingWizardKeys.draft(variables.listingId),
      });
    },
    onError: (error) => {
      toast.error("Failed to upload image");
      console.error(error);
    },
  });

  // Step 6: Update title and description
  const updateTitleDescMutation = useMutation({
    mutationFn: ({
      listingId,
      data,
    }: {
      listingId: number;
      data: { title: string; description: string };
    }) => listingWizardService.updateTitleAndDescription(listingId, data),
    onSuccess: (data) => {
      // 'data' here is Listing
      toast.success("Title and description saved!");
      queryClient.setQueryData(
        listingWizardKeys.draft(Number(data.listing.id)),
        data
      );
    },
    onError: (error) => {
      toast.error("Failed to save title and description");
      console.error(error);
    },
  });

  // Step 7: Publish listing
  const publishListingMutation = useMutation({
    mutationFn: ({ listingId, data }: { listingId: number; data: any }) =>
      listingWizardService.publishListing(listingId, data),
    onSuccess: (data) => {
      // 'data' here is Listing
      toast.success("Listing published successfully!");
      queryClient.invalidateQueries({ queryKey: ["userListings"] }); // Invalidate user's listings
      queryClient.setQueryData(
        listingWizardKeys.draft(Number(data.listing.id)),
        data
      );
    },
    onError: (error) => {
      toast.error("Failed to publish listing");
      console.error(error);
    },
  });

  // Fetch a draft listing
  const useDraftListing = (id?: number) => {
    return useQuery({
      queryKey: listingWizardKeys.draft(id),
      queryFn: () => (id ? listingWizardService.getListing(id) : null),
      enabled: !!id,
    });
  };

  return {
    // Mutations
    createDraftMutation,
    updateDetailsMutation,
    updateLocationMutation,
    uploadImageMutation,
    deleteImageMutation,
    setPrimaryImageMutation,
    updateContactMutation,
    updateTitleDescMutation,
    publishListingMutation,

    // Queries
    useDraftListing,
  };
}
